import React, { useEffect, useState } from 'react';

interface ProgressUpdateAnimationProps {
  isVisible: boolean;
  onComplete: () => void;
  theme: any;
  value: number;
  totalPoints: number;
  // NEW: Add measurement-related props for better display
  measurementValue?: number;
  measurementTarget?: number;
  measurementUnit?: string;
  isMeasurementMode?: boolean;
}

export const ProgressUpdateAnimation: React.FC<ProgressUpdateAnimationProps> = ({
  isVisible,
  onComplete,
  theme,
  value,
  totalPoints,
  measurementValue,
  measurementTarget,
  measurementUnit,
  isMeasurementMode = false
}) => {
  const [showAnimation, setShowAnimation] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'entering' | 'progress' | 'confetti' | 'exiting'>('entering');

  useEffect(() => {
    if (isVisible) {
      setShowAnimation(true);
      setAnimationPhase('entering');

      // Animation sequence
      const timer1 = setTimeout(() => setAnimationPhase('progress'), 300);
      const timer2 = setTimeout(() => setAnimationPhase('confetti'), 800);
      const timer3 = setTimeout(() => setAnimationPhase('exiting'), 1800);
      const timer4 = setTimeout(() => {
        setShowAnimation(false);
        onComplete();
      }, 2100);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
        clearTimeout(timer4);
      };
    }
  }, [isVisible, onComplete]);

  // Use measurement values if available, otherwise fall back to points
  const displayValue = measurementValue !== undefined ? measurementValue : value;
  const displayTarget = measurementTarget !== undefined ? measurementTarget : totalPoints;
  const displayUnit = measurementUnit || 'points';
  const progressPercentage = Math.min((displayValue / displayTarget) * 100, 100);
  const isCompleted = displayValue >= displayTarget;

  if (!showAnimation) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10000,
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: '85%',
        maxWidth: '400px',
        backgroundColor: theme.colors.surface,
        borderRadius: '20px',
        padding: '24px',
        textAlign: 'center',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        transform: animationPhase === 'entering' ? 'scale(0.8) translateY(20px)' :
          animationPhase === 'exiting' ? 'scale(0.9) translateY(-10px)' : 'scale(1) translateY(0)',
        opacity: animationPhase === 'entering' ? 0 : animationPhase === 'exiting' ? 0 : 1,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        {/* Success Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: isCompleted ? theme.colors.success : theme.colors.primary,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '0 auto 16px',
          fontSize: '36px',
          transform: animationPhase === 'progress' ? 'scale(1.1)' : 'scale(1)',
          transition: 'transform 0.2s ease',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        }}>
          {isCompleted ? '🎉' : '📈'}
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          color: theme.colors.text,
          margin: '0 0 16px 0',
          transform: animationPhase === 'entering' ? 'translateY(10px)' : 'translateY(0)',
          opacity: animationPhase === 'entering' ? 0 : 1,
          transition: 'all 0.3s ease 0.1s',
        }}>
          {isCompleted ? 'Challenge Completed!' : 'Progress Updated!'}
        </h2>

        {/* Progress Display */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: theme.colors.textSecondary,
            marginBottom: '8px',
          }}>
            {displayValue} / {displayTarget} {displayUnit}
          </div>
          <div style={{
            height: '8px',
            backgroundColor: theme.colors.border,
            borderRadius: '4px',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <div style={{
              height: '100%',
              backgroundColor: isCompleted ? theme.colors.success : theme.colors.primary,
              borderRadius: '4px',
              width: animationPhase === 'progress' ? `${progressPercentage}%` : '0%',
              transition: 'width 0.5s ease 0.3s',
            }} />
          </div>
        </div>

        {/* Message */}
        <p style={{
          fontSize: '16px',
          color: theme.colors.textSecondary,
          lineHeight: '1.5',
          margin: '0',
          transform: animationPhase === 'entering' ? 'translateY(10px)' : 'translateY(0)',
          opacity: animationPhase === 'entering' ? 0 : 1,
          transition: 'all 0.3s ease 0.2s',
        }}>
          {isCompleted
            ? 'Amazing work! You\'ve completed today\'s goal! 🎊'
            : 'Great progress! Keep up the momentum! 💪'
          }
        </p>

        {/* Confetti Effect */}
        {animationPhase === 'confetti' && (
          <div style={{
            position: 'absolute',
            top: '-20px',
            left: '0',
            right: '0',
            bottom: '0',
            pointerEvents: 'none',
          }}>
            {['🎉', '✨', '🌟', '💫', '⭐', '🎊'].map((emoji, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  fontSize: '20px',
                  left: '50%',
                  top: '50%',
                  transform: `translate(-50%, -50%) translate(${(i % 2 === 0 ? 1 : -1) * (20 + i * 10)}px, ${-30 - i * 8}px)`,
                  animation: `confettiFall${i} 1s ease-out ${i * 0.1}s forwards`,
                }}
              >
                {emoji}
              </div>
            ))}
          </div>
        )}

        <style>{`
          @keyframes confettiFall0 {
            0% { transform: translate(-50%, -50%) translate(20px, 0px) rotate(0deg); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(20px, -30px) rotate(360deg); opacity: 0; }
          }
          @keyframes confettiFall1 {
            0% { transform: translate(-50%, -50%) translate(-30px, 0px) rotate(0deg); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-30px, -38px) rotate(360deg); opacity: 0; }
          }
          @keyframes confettiFall2 {
            0% { transform: translate(-50%, -50%) translate(40px, 0px) rotate(0deg); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(40px, -46px) rotate(360deg); opacity: 0; }
          }
          @keyframes confettiFall3 {
            0% { transform: translate(-50%, -50%) translate(-50px, 0px) rotate(0deg); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-50px, -54px) rotate(360deg); opacity: 0; }
          }
          @keyframes confettiFall4 {
            0% { transform: translate(-50%, -50%) translate(60px, 0px) rotate(0deg); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(60px, -62px) rotate(360deg); opacity: 0; }
          }
          @keyframes confettiFall5 {
            0% { transform: translate(-50%, -50%) translate(-70px, 0px) rotate(0deg); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-70px, -70px) rotate(360deg); opacity: 0; }
          }
        `}</style>
      </div>
    </div>
  );
}; 